import { Form, TextArea } from '@mairistem/composants';
import {
	type Bordereau,
	type CodeServiceTva,
	EntiteExercice,
	type Exercice,
	type Mouvement,
	type ParametreGeneralEntite,
	useService,
} from '@mairistem/comptabilite';
import { Combobox, isHorizon } from '@mairistem/finances-utils';
import { isNewIdentifiant } from '@mairistem/tools';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type BaseProfilEcritureMouvement from '../../../../classes/mouvements/profilEcriture/Base/BaseProfilEcritureMouvement';
import AUTO from '../../../../constants/common/common';
import StorageKey from '../../../../constants/common/storageKey';
import CODE_BHM from '../../../../constants/mouvements/codeBHM';
import type { GroupeProfilEcriture } from '../../../../constants/mouvements/groupesProfilEcriture';
import useLiquidationTreeviewStore from '../../../../stores/liquidationTreeviewStore';
import BordereauSelect from '../../../Liquidation/LiquidationTreeView/Bordereau/BordereauSelect';
import { getDureeFromTiersOrExercice } from '../Complements/DelaiPaiement/delaiPaiementUtils';
import BordereauModal from './BordereauModal';
import ProfilEcritureCombobox from './ProfilEcritureCombobox';

interface InformationsGeneralesProps {
	codesServiceTva: CodeServiceTva[];
	groupeProfilEcriture: GroupeProfilEcriture;
	identifiant: number | string;
	isDirty: React.MutableRefObject<boolean>;
	mouvement: Mouvement;
	onChange: (changes: Partial<Mouvement>) => void;
	onChangeProfilEcriture: (codeProfilEcriture: string) => void;
	onClear: (codeProfilEcriture: string) => void;
	profilEcriture: BaseProfilEcritureMouvement;
}

const InformationsGenerales = ({
	codesServiceTva,
	groupeProfilEcriture,
	identifiant,
	isDirty,
	mouvement,
	onChange,
	onChangeProfilEcriture,
	onClear,
	profilEcriture,
}: InformationsGeneralesProps) => {
	const [open, setOpen] = useState(false);
	const resolveCreateBordereau = useRef<
		((bordereau: Bordereau) => void) | null
	>(null);

	const [ServiceCombobox, isServiceComboboxVisible] = useService();

	// Mettre a jour le session storage selon le dernier budget séléctionné
	useEffect(() => {
		if (
			mouvement?.exercice?.entite?.identifiantEntite &&
			mouvement?.exercice?.identifiant
		) {
			sessionStorage.setItem(
				StorageKey.IDENTIFIANT_ENTITE,
				String(mouvement?.exercice?.entite?.identifiantEntite),
			);
			sessionStorage.setItem(
				StorageKey.IDENTIFIANT_EXERCICE,
				String(mouvement?.exercice?.identifiant),
			);
		}
	}, [
		mouvement?.exercice?.entite?.identifiantEntite,
		mouvement?.exercice?.identifiant,
	]);

	const handleSelectBudget = useCallback(
		(entite: ParametreGeneralEntite, exercice: Exercice) => {
			onChange({
				...getDureeFromTiersOrExercice(exercice, mouvement?.tiers),
				identifiantExercice: exercice?.identifiant,
				identifiantEntite: entite?.identifiantEntite,
				exercice: {
					...exercice,
					entite,
				},
			});
		},
		[mouvement?.tiers, onChange],
	);

	const handleChangeCodeServiceTva = useCallback(
		(_e, { name, value }) => {
			onChange({
				[name]: value,
				// Aligne la TVA récup sur toutes les lignes (en base ou non) au (dé)choix du code service TVA
				...(mouvement?.lignes?.length && {
					lignes: mouvement.lignes.map((ligne) => ({
						...ligne,
						tvaRecup: !!value,
					})),
				}),
			});
		},
		[mouvement?.lignes, onChange],
	);

	const handleSelectServiceUtilisateur = useCallback(
		(identifiantService: number) => {
			onChange({ identifiantService });
		},
		[onChange],
	);

	const handleChange = useCallback(
		(_e, { name, value }) => {
			onChange({ [name]: value });
		},
		[onChange],
	);

	const renderBudget = useMemo(
		() => (
			<Form.Field
				control={EntiteExercice}
				disabled={!isNewIdentifiant(identifiant)}
				entite={mouvement?.exercice?.entite ?? {}}
				exercice={mouvement?.exercice ?? {}}
				label="Budget"
				onSelect={handleSelectBudget}
				width={7}
			/>
		),
		[handleSelectBudget, identifiant, mouvement?.exercice],
	);

	const renderProfilEcriture = useMemo(
		() => (
			<Form.Field
				codeGroupeProfilEcriture={groupeProfilEcriture?.code}
				codeProfil={profilEcriture?.code}
				control={ProfilEcritureCombobox}
				identifiant={identifiant}
				isDirty={isDirty}
				label="Profil d'écriture"
				mouvement={mouvement}
				onChange={onChangeProfilEcriture}
				onClear={onClear}
				width={7}
			/>
		),
		[
			groupeProfilEcriture?.code,
			identifiant,
			isDirty,
			mouvement,
			onChangeProfilEcriture,
			onClear,
			profilEcriture?.code,
		],
	);

	const renderServiceUtilisateur = useMemo(
		() => (
			<Form.Field
				control={ServiceCombobox}
				identifiantService={mouvement?.identifiantService}
				label="Service utilisateur"
				onSelect={handleSelectServiceUtilisateur}
				required
				width={5}
			/>
		),
		[
			ServiceCombobox,
			handleSelectServiceUtilisateur,
			mouvement?.identifiantService,
		],
	);

	const renderServiceTva = useMemo(
		() => (
			<Form.Field
				clearable
				control={Combobox}
				data-testid="identifiantCodeServiceTva"
				label="Service TVA"
				name="identifiantCodeServiceTva"
				onChange={handleChangeCodeServiceTva}
				options={codesServiceTva.map((code) => ({
					'data-testid': `codeServiceTva${code.codeServiceTva}`,
					key: code.identifiant,
					text: `${code.codeServiceTva} - ${code.libelle}`,
					value: code.identifiant,
				}))}
				placeholder="Hors service assujetti"
				selection
				value={mouvement?.identifiantCodeServiceTva ?? ''}
				width={isHorizon() ? 9 : 4}
			/>
		),
		[
			codesServiceTva,
			handleChangeCodeServiceTva,
			mouvement?.identifiantCodeServiceTva,
		],
	);

	const renderMouvementNumero = useMemo(
		() => (
			<Form.Input
				data-testid="numeroMouvement"
				label="Numéro"
				placeholder={AUTO}
				readOnly
				value={mouvement?.numero ?? ''}
				width={2}
			/>
		),
		[mouvement?.numero],
	);

	const renderObjet = useMemo(
		() => (
			<Form.Field
				id="objetPiece"
				control={TextArea}
				data-testid="objetMouvement"
				label="Objet"
				maxLength={160}
				name="objetPiece"
				onChange={handleChange}
				rows={5}
				value={mouvement?.objetPiece ?? ''}
				width={7}
			/>
		),
		[handleChange, mouvement?.objetPiece],
	);

	const renderReferencePiecesJustificatives = useMemo(
		() => (
			<Form.Field
				control={TextArea}
				data-testid="referencePiecesJustificatives"
				label="Référence des pièces justificatives"
				maxLength={250}
				name="pieceJointe"
				onChange={handleChange}
				rows={5}
				value={mouvement?.pieceJointe ?? ''}
				width={9}
			/>
		),
		[handleChange, mouvement?.pieceJointe],
	);

	const handleClose = (data) => {
		setOpen(false);
		if (resolveCreateBordereau.current) {
			resolveCreateBordereau.current(data);
			resolveCreateBordereau.current = null;
		}
	};

	// créé tout de suite, même si la pièce n'est pas enregistrée : la treeview l'ajoutera à son entité au retour
	const handleBordereauCree = (bordereau: Partial<Bordereau>): void => {
		useLiquidationTreeviewStore
			.getState()
			.signalerBordereau(
				Number(bordereau.identifiant),
				bordereau.identifiantExercice,
			);
	};

	const handleCreateBordereau = (): Promise<Bordereau> => {
		setOpen(true);
		// Retourner une Promise qui se résout quand la modale se ferme
		return new Promise((resolve) => {
			resolveCreateBordereau.current = resolve;
		});
	};

	const handleChangeBordereau = useCallback(
		(bordereau: Bordereau) => {
			onChange({
				bordereau,
				identifiantBordereau: bordereau?.identifiant as number,
				...(mouvement?.identifiantBordereau !== bordereau?.identifiant
					? { numero: null }
					: {}),
			});
		},
		[mouvement?.identifiantBordereau, onChange],
	);

	return (
		<>
			<Form>
				<Form.Group>
					{renderBudget}
					<BordereauSelect
						identifiant={identifiant}
						mouvement={mouvement}
						onChange={handleChangeBordereau}
						onCreateBordereau={handleCreateBordereau}
						profilEcriture={profilEcriture}
						value={mouvement?.identifiantBordereau ?? -1}
						data-testid="identifiantBordereau"
						width={7}
						label="Bordereau"
						disabled={mouvement?.codeBHM === CODE_BHM.VALUE.HISTORISE}
					/>
					{renderMouvementNumero}
				</Form.Group>
				<Form.Group>
					{renderProfilEcriture}
					{renderServiceTva}
					{!isHorizon() && isServiceComboboxVisible && renderServiceUtilisateur}
				</Form.Group>
				<Form.Group>
					{renderObjet}
					{renderReferencePiecesJustificatives}
				</Form.Group>
			</Form>
			{open && (
				<BordereauModal
					bordereau={{
						sens: mouvement?.sens,
						codeBHM: mouvement?.codeBHM,
						piecesCount: 0,
					}}
					entite={mouvement?.exercice?.entite}
					exercice={mouvement?.exercice}
					isCreation
					numeroDisabled
					onConfirm={handleBordereauCree}
					onClose={handleClose}
					open={open}
				/>
			)}
		</>
	);
};

export default InformationsGenerales;
